// #region Imports
import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchTransferSla,
    patchTransferOverride,
    resetTransferSla,
    resetPatchTransferOverrideStatus,
    selectTransferSlaData,
    selectTransferSlaStatus,
    selectTransferSlaError,
    selectPatchTransferOverrideStatus,
} from 'redux/slices/sla-reports/transfer-sla';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import TransferSlaSummary from './transfer-sla-summary';
import TransferSlaRawDataTable from './transfer-sla-raw-data-table';
import TransferSlaAttentionPanel from './transfer-sla-attention-panel';
import ReportHeader from './report-header';
// #endregion

// #region TransferSlaReport Component
/**
 * Transfer SLA Report - inbound bookings with a populated `booking.inbound`
 * sub-object (transit pax), split into Pre-Advised vs Non Pre-Advised. Each
 * booking auto-passes when assisted; overrides write to `transferSlaOverride`
 * keyed by `preAdvised` / `nonPreAdvised`.
 */
const TransferSlaReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {

    // #region State
    const [overrideContext, setOverrideContext] = useState(null);
    // #endregion

    // #region Redux State
    const dispatch = useDispatch();
    const data = useSelector(selectTransferSlaData);
    const status = useSelector(selectTransferSlaStatus);
    const error = useSelector(selectTransferSlaError);
    const patchStatus = useSelector(selectPatchTransferOverrideStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';
    // #endregion

    // #region handlers
    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchTransferSla({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);
    // #endregion

    // #region Effects
    useEffect(() => {
        return () => {
            dispatch(resetTransferSla());
        };
    }, [dispatch]);

    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchTransferOverrideStatus());
            setOverrideContext(null);
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchTransferSla({ startDate, endDate, iata, hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchTransferOverrideStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);
    // #endregion

    // #region Derived Data
    const dailyReports = data?.dailyReports ?? [];
    const isMultiDay = (data?.dateRange?.length ?? 0) > 1;
    // #endregion

    // #region Render
    return (
        <div>

            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
            />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading Transfer SLA report: {error}</p>
            )}

            {data && (
                <>
                    <TransferSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={isMultiDay ? data.summary : null}
                    />
                    <TransferSlaAttentionPanel
                        unpairedTransfers={data.unpairedTransfers ?? []}
                        unassistedTransfers={data.unassistedTransfers ?? []}
                        hideBreakdown={hideBreakdown}
                    />
                    {!hideBreakdown && (
                        <TransferSlaRawDataTable
                            dailyReports={dailyReports}
                            overrideContext={overrideContext}
                            onOverrideRowChange={setOverrideContext}
                            onPatchTransfer={(row, pass, passReason, { ignore } = {}) => {
                                if (row?.bookingId && overrideContext?.kpiKey) {
                                    dispatch(patchTransferOverride({
                                        bookingId: row.bookingId,
                                        kpiKey: overrideContext.kpiKey,
                                        pass,
                                        passReason,
                                        ignore: !!ignore,
                                    }));
                                }
                            }}
                            isPatching={isPatching}
                        />
                    )}
                </>
            )}

        </div>
    );
    // #endregion
};
// #endregion

export default TransferSlaReport;
